<template>
  <div class="reg-queue-container">
    <div class="content-section">
      <div class="section-header">
        <div class="section-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-6m8 0V9a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v4.01"/>
          </svg>
        </div>
        <div>
          <h2>Táncosok Regisztrációs Sorrendje</h2>
          <p class="section-subtitle">{{ isTeacher && !isAdmin ? 'Hozzárendelt csoportok regisztrációs sorrendje' : 'Regisztrációs sorrend és csoport beosztás' }}</p>
        </div>
      </div>
      
      <div class="section-content">
        <!-- Admin Registration Settings Quick-Toggle -->
        <div v-if="isAdmin" class="settings-toggle-card">
          <div class="toggle-card-body">
            <div class="toggle-card-info">
              <div class="toggle-title-row">
                <span class="toggle-title">Automatikus csoportba sorolás regisztrációkor:</span>
                <span :class="['status-chip', autoAssignGroup ? 'chip-active' : 'chip-inactive']">
                  {{ autoAssignGroup ? 'Bekapcsolva' : 'Kikapcsolva' }}
                </span>
              </div>
              <p class="toggle-desc">
                {{ autoAssignGroup
                  ? 'A regisztráló táncosok automatikusan hozzárendelésre kerülnek a választott kívánt csoporthoz.'
                  : 'A táncosok nem kerülnek automatikusan csoportba regisztrációkor; az alábbi táblázatban az adminisztrátorok és tanárok választhatják ki a végleges csoportjukat.' }}
              </p>
            </div>
            <div class="toggle-switch-wrapper">
              <label class="switch" :title="autoAssignGroup ? 'Kikapcsolás' : 'Bekapcsolás'">
                <input 
                  type="checkbox" 
                  v-model="autoAssignGroup" 
                  @change="toggleAutoAssign" 
                  :disabled="isSavingSettings"
                >
                <span class="slider round"></span>
              </label>
            </div>
          </div>
        </div>

        <!-- Filter & Search Controls -->
        <div class="filter-section">
          <div class="filter-group">
            <label for="groupFilter" class="filter-label">Szűrés kívánt csoport szerint:</label>
            <select v-model="selectedGroup" class="modern-select" id="groupFilter">
              <option value="">Összes csoport</option>
              <option v-for="group in groups" :key="group.id" :value="group.name">
                {{ group.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Table Section -->
        <div class="table-section">
          <div class="table-header">
            <h3>Regisztrációs Lista</h3>
            <span class="count-badge">{{ filteredChildren.length }} táncos</span>
          </div>
          
          <div class="modern-table-container">
            <table class="modern-table">
              <thead>
                <tr>
                  <th style="width: 50px;">#</th>
                  <th>Név</th>
                  <th>Kívánt csoport</th>
                  <th>Jelenlegi csoport</th>
                  <th style="min-width: 250px;">Csoport kiválasztása & beosztás</th>
                  <th style="text-align: right;">Adatlap</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="child in filteredChildren" 
                  :key="child.child_id" 
                  class="table-row"
                >
                  <td class="position-cell">{{ child.reg_place }}</td>
                  <td class="name-cell font-weight-bold">
                    {{ child.child_family_name }} {{ child.child_sure_name }}
                  </td>
                  <td>
                    <span v-if="child.desired_group_name" class="group-badge group-badge--desired">
                      {{ child.desired_group_name }}
                    </span>
                    <span v-else class="text-muted">Nincs megadva</span>
                  </td>
                  <td>
                    <span v-if="child.current_group_name" class="group-badge group-badge--current">
                      {{ child.current_group_name }}
                    </span>
                    <span v-else class="badge-unassigned">
                      Nincs beosztva
                    </span>
                  </td>
                  <td>
                    <div class="assign-group-controls" @click.stop>
                      <select 
                        v-model="child.selectedGroupId" 
                        class="modern-select-sm"
                        :disabled="child.isSaving"
                      >
                        <option value="">Válassz csoportot...</option>
                        <option 
                          v-for="group in availableGroupsForAssignment" 
                          :key="group.id" 
                          :value="group.id"
                        >
                          {{ group.name }}
                        </option>
                      </select>
                      <button 
                        @click="assignGroup(child)" 
                        class="modern-btn-sm modern-btn--primary"
                        :disabled="!child.selectedGroupId || child.isSaving"
                      >
                        <span v-if="child.isSaving">Mentés...</span>
                        <span v-else-if="child.successMsg">✓ Sikeres!</span>
                        <span v-else-if="child.current_group_name">Módosítás</span>
                        <span v-else>Beosztás</span>
                      </button>
                    </div>
                  </td>
                  <td style="text-align: right;">
                    <button 
                      @click="goToUniqueChildPage(child)" 
                      class="modern-btn-sm modern-btn--outline"
                    >
                      Adatlap &rarr;
                    </button>
                  </td>
                </tr>
                <tr v-if="filteredChildren.length === 0">
                  <td colspan="6" class="text-center py-4 text-muted">
                    Nincs a keresési feltételeknek megfelelő táncos a várólistán.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
  
<script>
import axios from 'axios';

export default {
  name: 'ChildRegQueueItem',
  data() {
    return {
      children: [],
      groups: [],
      teacherGroups: [],
      selectedGroup: '', // Stores the selected group name for filter
      isAdmin: false,
      isTeacher: false,
      userId: null,
      autoAssignGroup: false,
      isSavingSettings: false,
      customSettings: null,
    };
  },
  computed: {
    filteredChildren() {
      if (!this.selectedGroup) {
        return this.children;
      }
      return this.children.filter(child => 
        child.desired_group_name === this.selectedGroup || 
        child.current_group_name === this.selectedGroup
      );
    },
    availableGroupsForAssignment() {
      if (this.isAdmin) {
        return this.groups;
      }
      if (this.isTeacher && this.teacherGroups.length > 0) {
        return this.teacherGroups;
      }
      return this.groups;
    }
  },
  methods: {
    getIsAdmin() {
      this.isAdmin = this.$isAdmin();
    },
    getIsTeacher() {
      this.isTeacher = this.$isTeacher();
    },
    getUserId() {
      this.userId = this.$getUserId();
    },
    async fetchAutoAssignSetting() {
      if (!this.isAdmin) return;
      try {
        const response = await axios.get(`${this.$BACKEND_HOST}/api/get-cust-settings`);
        if (response.data && response.data.custom_settings) {
          this.customSettings = response.data.custom_settings;
          this.autoAssignGroup = !!response.data.custom_settings.registration?.auto_assign_group;
        }
      } catch (error) {
        console.error('Error fetching auto assign setting:', error);
      }
    },
    async toggleAutoAssign() {
      if (!this.isAdmin) return;
      this.isSavingSettings = true;
      try {
        if (!this.customSettings) this.customSettings = {};
        if (!this.customSettings.registration) this.customSettings.registration = {};
        this.customSettings.registration.auto_assign_group = this.autoAssignGroup;
        await axios.post(`${this.$BACKEND_HOST}/api/save-cust-settings`, this.customSettings);
      } catch (e) {
        console.error('Error saving auto assign setting:', e);
        this.autoAssignGroup = !this.autoAssignGroup;
        alert('Hiba történt a beállítás mentésekor.');
      } finally {
        this.isSavingSettings = false;
      }
    },
    parseCurrentGroupId(child) {
      if (!child.child_current_group_id) return null;
      if (typeof child.child_current_group_id === 'object') {
        return child.child_current_group_id.Valid ? child.child_current_group_id.Int64 : null;
      }
      if (typeof child.child_current_group_id === 'number' && child.child_current_group_id > 0) {
        return child.child_current_group_id;
      }
      return null;
    },
    async fetchGroups() {
      try {
        const response = await fetch(`${this.$BACKEND_HOST}/api/settings/children/register-queue`);
        const data = await response.json();
        const rawQueue = data.reg_queue || [];
        
        // If user is a teacher, filter to only show children from their assigned groups
        if (this.isTeacher && !this.isAdmin) {
          const teacherId = this.userId;
          if (teacherId) {
            const teacherResponse = await fetch(`${this.$BACKEND_HOST}/api/settings/teachers/groups?teacher_id=${teacherId}`);
            if (teacherResponse.ok) {
              const teacherData = await teacherResponse.json();
              this.teacherGroups = teacherData.groups || [];
              const assignedGroupIds = this.teacherGroups.map(g => g.id);

              if (assignedGroupIds.length === 0) {
                this.children = rawQueue;
              } else {
                this.children = rawQueue.filter(child => {
                  const currentId = this.parseCurrentGroupId(child);
                  return (
                    assignedGroupIds.includes(child.child_desired_group) ||
                    (currentId != null && assignedGroupIds.includes(currentId))
                  );
                });
              }
            } else {
              this.children = [];
            }
          } else {
            this.children = rawQueue;
          }
        } else {
          this.children = rawQueue;
        }
        
        await this.getGroups();
      } catch (error) {
        console.error("Error fetching groups:", error);
      }
    },
    async getGroups() {
      try {
        const response = await fetch(`${this.$BACKEND_HOST}/api/settings/groups/info`);
        const data = await response.json();
        this.groups = data.groups || [];

        if (this.children.length > 0) {
          let i = 1;
          this.children.forEach(child => {
            child.reg_place = i++;
            // Desired group name
            const desiredGroup = this.groups.find(g => g.id === child.child_desired_group);
            child.desired_group_name = desiredGroup ? desiredGroup.name : '';
            
            // Current assigned group name
            const currentId = this.parseCurrentGroupId(child);
            const currentGroup = currentId ? this.groups.find(g => g.id === currentId) : null;
            child.current_group_name = currentGroup ? currentGroup.name : '';

            // Selected group in dropdown (defaults to current group, otherwise desired group)
            child.selectedGroupId = currentId || child.child_desired_group || '';
            child.isSaving = false;
            child.successMsg = false;
          });
        }
      } catch (error) {
        console.error("Error fetching group info:", error);
      }
    },
    async assignGroup(child) {
      if (!child.selectedGroupId) return;
      child.isSaving = true;
      try {
        const targetGroupId = parseInt(child.selectedGroupId);
        const res = await axios.post(`${this.$BACKEND_HOST}/api/child/groups/add`, {
          child_id: child.child_id,
          group_id: targetGroupId,
          is_primary: true
        });

        if (res.status === 200) {
          child.child_current_group_id = { Int64: targetGroupId, Valid: true };
          const g = this.groups.find(grp => grp.id === targetGroupId);
          child.current_group_name = g ? g.name : '';
          child.successMsg = true;
          setTimeout(() => {
            child.successMsg = false;
          }, 2500);
        }
      } catch (err) {
        console.error('Error assigning group:', err);
        alert(err.response?.data?.message || 'Hiba történt a csoport hozzárendelésekor.');
      } finally {
        child.isSaving = false;
      }
    },
    goToUniqueChildPage(child) {
      this.$router.push(`/children/${child.child_id}`);
    },
  },
  mounted() {
    this.getIsAdmin();
    this.getIsTeacher();
    this.getUserId();
    this.fetchGroups();
    this.fetchAutoAssignSetting();
  },
};
</script>
  
<style scoped>
.reg-queue-container {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.content-section {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 2rem;
  background: var(--color-background-mute);
  border-bottom: 1px solid var(--color-border);
}

.section-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 10px;
  color: white;
  flex-shrink: 0;
}

.section-header h2 {
  color: var(--color-heading);
  font-size: 1.4rem;
  font-weight: 600;
  margin: 0;
}

.section-subtitle {
  color: var(--color-text-muted);
  font-size: 0.9rem;
  margin: 0.2rem 0 0 0;
}

.section-content {
  padding: 2rem;
}

/* Admin Registration Settings Card */
.settings-toggle-card {
  background: var(--color-background-soft, #f8fafc);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 2rem;
}

.toggle-card-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
}

.toggle-card-info {
  flex: 1;
}

.toggle-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.35rem;
}

.toggle-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--color-heading);
}

.status-chip {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.chip-active {
  background: #dcfce7;
  color: #166534;
}

.chip-inactive {
  background: #fee2e2;
  color: #991b1b;
}

.toggle-desc {
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-text-muted);
  line-height: 1.4;
}

/* Switch Styles */
.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
  flex-shrink: 0;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: .3s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .3s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

input:checked + .slider {
  background-color: #3b82f6;
}

input:focus + .slider {
  box-shadow: 0 0 1px #3b82f6;
}

input:checked + .slider:before {
  transform: translateX(22px);
}

.slider.round {
  border-radius: 28px;
}

.slider.round:before {
  border-radius: 50%;
}

/* Filter Section */
.filter-section {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--color-border);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 320px;
}

.filter-label {
  color: var(--color-heading);
  font-size: 0.9rem;
  font-weight: 500;
  margin: 0;
}

.modern-select {
  padding: 0.7rem 1rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--card-bg);
  color: var(--color-text);
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s;
}

.modern-select:focus {
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

/* Table Section */
.table-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-header h3 {
  color: var(--color-heading);
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
}

.count-badge {
  background: var(--primary-blue, #3b82f6);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
}

.modern-table-container {
  background: var(--color-background-mute);
  border-radius: 12px;
  overflow-x: auto;
  border: 1px solid var(--color-border);
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--card-bg);
}

.modern-table thead {
  background: var(--color-background-mute);
}

.modern-table th {
  padding: 1rem;
  text-align: left;
  color: var(--color-heading);
  font-weight: 600;
  font-size: 0.9rem;
  border-bottom: 1px solid var(--color-border);
  white-space: nowrap;
}

.modern-table td {
  padding: 0.9rem 1rem;
  color: var(--color-text);
  font-size: 0.9rem;
  border-bottom: 1px solid var(--color-border);
  vertical-align: middle;
}

.table-row:hover {
  background: var(--color-background-mute);
}

.table-row:last-child td {
  border-bottom: none;
}

.position-cell {
  font-weight: 600;
  color: var(--primary-blue, #3b82f6);
  text-align: center;
}

.name-cell {
  color: var(--color-heading);
}

.group-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
}

.group-badge--desired {
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #cbd5e1;
}

.group-badge--current {
  background: #dbeafe;
  color: #1e40af;
  border: 1px solid #bfdbfe;
}

.badge-unassigned {
  display: inline-block;
  padding: 0.25rem 0.65rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
}

/* Controls inside row */
.assign-group-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.modern-select-sm {
  padding: 0.4rem 0.65rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--card-bg);
  color: var(--color-text);
  font-size: 0.85rem;
  outline: none;
  min-width: 150px;
}

.modern-select-sm:focus {
  border-color: var(--primary-blue, #3b82f6);
}

.modern-btn-sm {
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  font-size: 0.825rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s;
  white-space: nowrap;
}

.modern-btn--primary {
  background: #3b82f6;
  color: white;
}

.modern-btn--primary:hover:not(:disabled) {
  background: #2563eb;
}

.modern-btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modern-btn--outline {
  background: transparent;
  border-color: var(--color-border);
  color: var(--color-text);
}

.modern-btn--outline:hover {
  background: var(--color-background-soft);
  border-color: var(--color-text-muted);
}

/* Responsive */
@media (max-width: 768px) {
  .reg-queue-container {
    padding: 1rem;
  }
  .toggle-card-body {
    flex-direction: column;
    align-items: flex-start;
  }
  .assign-group-controls {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>